
import Vue from "vue";
import { Component, Prop, Inject } from "vue-property-decorator";

import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import BreakpointInput from "@/components/Reusable/CustomInputs/BreakpointInput.vue";
import DynamicInput from "@/components/Reusable/CustomInputs/DynamicInput.vue";
import UnitInput from "@/components/Reusable/CustomInputs/UnitInput/UnitInput.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import ConditionInput from "@/components/Reusable/CustomInputs/ConditionInput/ConditionInput.vue";
import FontFamilyInput from "@/components/Reusable/CustomInputs/FontFamily/FontFamilyInput.vue";
import SpacingComplex from "@/components/Reusable/CustomInputs/SpacingComplex.vue";
import ColorInput from "@/components/Reusable/CustomInputs/Color/ColorInput.vue";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import BorderComplex from "@/components/Reusable/CustomInputs/BorderComplex.vue";
import BorderRadius from "@/components/Reusable/CustomInputs/BorderRadius.vue";
import ClassInput from "@/components/Reusable/CustomInputs/ClassInput/ClassInput.vue";
import IconInput from "@/components/Reusable/CustomInputs/IconInput/IconInput.vue";
import GlobalBlockInput from "@/components/Reusable/CustomInputs/GlobalBlockInput.vue";
import MultiselectInput from "@/components/Reusable/CustomInputs/MultiselectInput.vue";
import RichTextInput from "@/components/Reusable/CustomInputs/RichTextInput/RichTextInput.vue";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import {
  Control,
  ControlsTreeContext,
  ValueEmittedFromControlInputDecoratedWithMetadata
} from "@/types/controls";
import GlobalColorsInput from "@/components/Reusable/CustomInputs/GlobalColorsInput.vue";
import MessageControl from "@/components/Reusable/CustomInputs/MessageControl.vue";
import ShadowInput from "@/components/Reusable/CustomInputs/ShadowInput/ShadowInput.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import URLInput from "@/components/Reusable/CustomInputs/LinkInput/URLInput.vue";
import FocusPointInput from "@/components/Reusable/CustomInputs/FocusPointInput/FocusPointInput.vue";
import InlineRepeater from "@/components/Reusable/CustomInputs/InlineRepeater.vue";
import TypographyInput from "@/components/Reusable/CustomInputs/Typography/TypographyInput.vue";
import TypographyPresetLabelAndIdInput from "@/components/Reusable/CustomInputs/Typography/TypographyPresetLabelAndIdInput.vue";
import ElementScreenshot from "@/components/Reusable/CustomInputs/ElementScreenshot.vue";
import VideoInput from "@/components/Reusable/CustomInputs/VideoInput.vue";
import LinkInput from "@/components/Reusable/CustomInputs/LinkInput/LinkInput.vue";
import { unwrapControlValue } from "@/lib/controls/utils";
import SliderInput from "@/components/Reusable/CustomInputs/SliderInput.vue";
import MediaSizeInput from "@/components/Reusable/CustomInputs/MediaSize/MediaSizeInput.vue";
import WpQueryControl from "@/components/Reusable/CustomInputs/WpQuery/WpQuery.vue";
import CustomTypographyInput from "@/components/Reusable/CustomInputs/Typography/CustomTypographyInput.vue";
import PostChooserInput from "@/components/Reusable/CustomInputs/PostChooserInput.vue";
import AddRegisteredChildrenControl from "@/components/Reusable/CustomInputs/AddRegisteredChildrenControl.vue";
import TriggerActionButton from "@/components/Reusable/CustomInputs/TriggerActionButton.vue";
import BuilderBehaviorInput from "@/components/Reusable/CustomInputs/BuilderBehaviorInput.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import DatePickerInput from "@/components/Reusable/CustomInputs/DatePickerInput.vue";
import TimePickerInput from "@/components/Reusable/CustomInputs/TimePickerInput.vue";
import ApiKeyInput from "@/components/Reusable/CustomInputs/ApiKeyInput.vue";
import { clear } from "@/lib/controls/values";
import { DynamicFieldOutput } from "@/types/dynamic-data";
import ConditionalFormFieldInput from "@/components/Reusable/CustomInputs/ConditionalFieldInput.vue";
import { getDynamicMetaPath } from "@/lib/dynamic-data/utils";
import ProOnlyAlertBox from "@/components/Subscription/ProOnlyAlertBox.vue";

@Component({
  components: {
    ProOnlyAlertBox,
    ConditionalFormFieldInput,
    TimePickerInput,
    DatePickerInput,
    ApiKeyInput,
    BuilderBehaviorInput,
    TriggerActionButton,
    AddRegisteredChildrenControl,
    PostChooserInput,
    CustomTypographyInput,
    SliderInput,
    MediaSizeInput,
    WpQueryControl,
    VideoInput,
    LinkInput,
    TypographyPresetLabelAndIdInput,
    ElementScreenshot,
    NumberInput,
    URLInput,
    FocusPointInput,
    ShadowInput,
    RichTextInput,
    ToggleInput,
    GlobalColorsInput,
    InlineRepeater,
    MultiselectInput,
    BreakpointInput,
    TextInput,
    UnitInput,
    DropdownInput,
    ColorInput,
    WPMediaInput,
    DynamicInput,
    ConditionInput,
    FontFamilyInput,
    SpacingComplex,
    BorderComplex,
    BorderRadius,
    ButtonBarInput,
    CodeInput,
    ClassInput,
    IconInput,
    GlobalBlockInput,
    MessageControl,
    TypographyInput,
    AlertBox
  }
})
export default class InputWrapper extends Vue {
  @Prop()
  control!: Control;

  @Prop()
  value: unknown;

  @Prop()
  path!: string;

  @Prop()
  dynamicField?: DynamicFieldOutput | null;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.Repeater =
      require("@/components/Reusable/CustomInputs/Repeater/Repeater.vue").default;
    // @ts-ignore
    this.$options.components.ControlsTree =
      require("@/components/Reusable/ControlsTree/ControlsTree.vue").default;
  }

  @Inject("context") readonly context!: ControlsTreeContext;

  edit(value: unknown) {
    const newValue = unwrapControlValue(value);

    if (value instanceof ValueEmittedFromControlInputDecoratedWithMetadata) {
      this.context.setControlValue(
        this.path,
        newValue,
        this.control,
        value.meta
      );
    } else {
      this.context.setControlValue(this.path, newValue, this.control);
    }
  }

  saveDynamicMetaData(value: DynamicFieldOutput) {
    this.context.setControlValue(
      getDynamicMetaPath(this.path),
      value,
      this.control
    );
  }

  get isDynamic() {
    return this.dynamicField !== undefined && this.dynamicField !== null;
  }

  get testId() {
    return `control-${this.path.replace(/\./g, "-")}`;
  }

  clear() {
    clear(this.path, this.control, this.context);
  }

  get parent() {
    const currentPath = this.path.split(".");
    currentPath.pop();
    return this.context.getControlValue(currentPath.join(".")) ?? null;
  }
}
